import React, { useState } from "react";
import { auth } from "../../config/firebase.js";
import { sendPasswordResetEmail } from "firebase/auth";
import { Link } from "react-router-dom";
import GoogleAnalytics from "../../analyticsComponents/GoogleAnalytics.js";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    setMessage("");
    setErrorMessage("");
    setIsSubmitting(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setMessage("Password reset email sent! Check your inbox (and spam folder).");
      setEmail("");
    } catch (err) {
      switch (err.code) {
        case "auth/user-not-found":
          setErrorMessage("No account found with this email address.");
          break;
        case "auth/invalid-email":
          setErrorMessage("Please enter a valid email address.");
          break;
        case "auth/too-many-requests":
          setErrorMessage("Too many requests. Please try again later.");
          break;
        default:
          setErrorMessage("Failed to send reset email. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100 py-20">
      <GoogleAnalytics page="ForgotPasswordPage" eventType="forgot_password_page" />

      <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
        <h2 className="text-2xl font-bold mb-2 text-center">Reset Your Password</h2>
        <p className="text-gray-500 text-center mb-6">
          Enter your email address and we'll send you a link to reset your password.
        </p>

        {message && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <p className="text-green-800 text-sm text-center">{message}</p>
          </div>
        )}

        {errorMessage && (
          <p className="text-red-500 text-center mb-4">{errorMessage}</p>
        )}

        <form onSubmit={handleReset} className="space-y-4">
          <div className="flex items-center border rounded-lg p-2">
            <span className="text-xl mr-2">📧</span>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full outline-none border-none"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-2 rounded-lg text-white font-semibold transition flex items-center justify-center gap-2 ${
              isSubmitting
                ? "bg-purple-400 cursor-not-allowed"
                : "bg-purple-600 hover:bg-purple-700"
            }`}
          >
            {isSubmitting && (
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            )}
            {isSubmitting ? "Sending…" : "Send Reset Email"}
          </button>
        </form>

        <p className="text-gray-500 mt-6 text-center">
          Remember your password?{" "}
          <Link
            to="/sign-in"
            className="text-purple-600 font-semibold hover:underline"
          >
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
